import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Button,
  Form,
  Sortable,
  SortableList,
  Well,
  Paragraph,
  Gutter,
  SectionHeader,
  Flashy,
  Badge,
} from '@axiom/ui';
import type {
  Candidate,
  CandidateProfileTopFocusAreas,
  Taxonomy,
} from '@axiom/validation';

import { CandidateProfileTopFocusAreasApi } from '../../api/candidate-profile-top-focus-areas-api';
import { CandidateProfileHighlightsApi } from '../../api/candidate-profile-highlights-api';

import { profileHighlightsTopFocusAreasModalFormSchema } from './profile-highlights-top-focus-areas-modal-form-schema';

export type ProfileHighlightsTopFocusAreasModalProps = {
  candidate: Candidate;
  name?: string;
  onClose: () => void;
  profileTopFocusAreas: CandidateProfileTopFocusAreas;
  taxonomy: Taxonomy;
};

export const ProfileHighlightsTopFocusAreasModal = ({
  candidate,
  name = 'PROFILE_HIGHLIGHTS_TOP_FOCUS_AREA_MODAL',
  onClose,
  profileTopFocusAreas,
  taxonomy,
}: ProfileHighlightsTopFocusAreasModalProps) => {
  const { topFocusAreas, otherFocusAreas, hiddenFocusAreas } =
    profileTopFocusAreas;

  const options = [
    ...topFocusAreas,
    ...otherFocusAreas,
    ...hiddenFocusAreas,
  ].map(focusArea => ({
    value: focusArea,
    label: taxonomy.focusAreas[focusArea]?.fullName,
  }));

  return (
    <Form
      name={name}
      schema={profileHighlightsTopFocusAreasModalFormSchema}
      initialValues={{
        topFocusAreas,
        otherFocusAreas,
        hiddenFocusAreas,
      }}
      onSubmit={async (changedData, actions, values) => {
        const body = {
          topFocusAreas: values.topFocusAreas,
          hiddenFocusAreas: values.hiddenFocusAreas,
        };

        await CandidateProfileTopFocusAreasApi.saveProfileTopFocusAreas(
          candidate.id,
          body
        );
        await CandidateProfileTopFocusAreasApi.refreshProfileTopFocusAreas(
          candidate.id
        );
        await CandidateProfileHighlightsApi.refreshProfileHighlights(
          candidate.id
        );
        onClose();
      }}
    >
      {({ fireSubmit, setValues, values }) => {
        return (
          <Modal>
            <ModalHeader
              name={`${name}_HEADER`}
              onClose={() => {
                onClose();
              }}
            >
              Edit Top Focus Areas
            </ModalHeader>
            <ModalSection>
              <Well background="blue">
                <SectionHeader>Rank your focus areas</SectionHeader>
                <Gutter bottom="8px" />
                <Paragraph>
                  These are the focus areas you’ve added to your profile
                  experiences.{' '}
                  <Flashy bold>Clients will only see your top 5.</Flashy>
                </Paragraph>
              </Well>
              <Gutter bottom="16px" />
              <Paragraph>
                Add up to 5 top focus areas that you want to show clients and
                Axiom, and that best represent you and the kind of work you
                want.
              </Paragraph>
              <Gutter bottom="16px" />
              <Paragraph>
                Highly specialized? Only one or two focus areas is totally fine.
              </Paragraph>
              <Gutter bottom="16px" />
              <Sortable options={options} displayKey="label" valueKey="value">
                <Well background="sky blue">
                  <Badge background="blue">Top Focus Areas</Badge>
                  <Gutter bottom="8px" />
                  <SortableList
                    name="topFocusAreas"
                    dismissible
                    dismissibleIcon="eye"
                    onAfterRemove={(value, updatedList) => {
                      const hidden: string[] = values.hiddenFocusAreas ?? [];
                      void setValues({
                        ...values,
                        topFocusAreas: updatedList,
                        hiddenFocusAreas: [...hidden, value as string],
                      });
                    }}
                  />
                </Well>
                <Gutter vertical="16px" horizontal="16px">
                  <SortableList
                    name="otherFocusAreas"
                    dismissible
                    dismissibleIcon="eye"
                    onAfterRemove={(value, updatedList) => {
                      const hidden = values.hiddenFocusAreas ?? [];
                      void setValues({
                        ...values,
                        otherFocusAreas: updatedList,
                        hiddenFocusAreas: [...hidden, value as string],
                      });
                    }}
                  />
                </Gutter>
              </Sortable>
              <Gutter vertical="16px">
                <ModalSection divider />
              </Gutter>
              <Flashy bold>
                <Paragraph>
                  You indicated no interest in these focus areas but you can
                  unhide any focus area if you change your mind.
                </Paragraph>
              </Flashy>
              <Gutter bottom="16px" />
              <Sortable options={options} displayKey="label" valueKey="value">
                <SortableList
                  name="hiddenFocusAreas"
                  dismissible
                  dismissibleIcon="eye-hidden"
                  hideHandle
                  onAfterRemove={(value, updatedList) => {
                    const other = values.otherFocusAreas ?? [];
                    void setValues({
                      ...values,
                      hiddenFocusAreas: updatedList,
                      otherFocusAreas: [...other, value as string],
                    });
                  }}
                />
              </Sortable>
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                name={`${name}_CANCEL`}
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button name={`${name}_SUBMIT`} onClick={fireSubmit}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
